/**
 * Validadores brasileiros.
 *
 * Vivem separados do Zod para poderem ser testados isoladamente e reaproveitados
 * em schema de criacao e de edicao, que sao diferentes por regra de negocio.
 */

/**
 * Valida CPF pelos dois digitos verificadores.
 *
 * Recusa as sequencias repetidas (`111.111.111-11`), que passam no calculo mas
 * nao sao CPFs reais.
 */
export function isValidCpf(value: string | null | undefined): boolean {
  if (!value) return false;
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;

  const checkDigit = (length: number): number => {
    let sum = 0;
    for (let i = 0; i < length; i += 1) {
      sum += Number(digits[i]) * (length + 1 - i);
    }
    const remainder = (sum * 10) % 11;
    return remainder === 10 ? 0 : remainder;
  };

  return checkDigit(9) === Number(digits[9]) && checkDigit(10) === Number(digits[10]);
}

/** Valida CEP: oito digitos, e nao todos iguais. */
export function isValidCep(value: string | null | undefined): boolean {
  if (!value) return false;
  const digits = value.replace(/\D/g, '');
  return digits.length === 8 && !/^(\d)\1{7}$/.test(digits);
}

/**
 * Valida telefone ou celular com DDD.
 *
 * DDD valido vai de 11 a 99; celular tem 11 digitos e comeca com 9 apos o DDD.
 */
export function isValidPhone(value: string | null | undefined): boolean {
  if (!value) return false;
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 10 && digits.length !== 11) return false;

  const ddd = Number(digits.slice(0, 2));
  if (ddd < 11 || ddd > 99) return false;

  return digits.length === 10 || digits[2] === '9';
}

/**
 * Valida data no formato `dd/mm/aaaa`, recusando dia inexistente no mes.
 *
 * `31/02/2026` passa numa checagem por regex e falha aqui.
 */
export function isValidDateBr(value: string | null | undefined): boolean {
  if (!value) return false;
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (!match) return false;

  const [day, month, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (month < 1 || month > 12 || day < 1) return false;

  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

/** Converte `dd/mm/aaaa` no `aaaa-mm-dd` que a API espera. */
export function dateBrToIso(value: string): string | null {
  if (!isValidDateBr(value)) return null;
  const [day, month, year] = value.split('/');
  return `${year}-${month}-${day}`;
}

/** Converte `aaaa-mm-dd` em `dd/mm/aaaa`. */
export function isoToDateBr(value: string | null | undefined): string {
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
}
